import React, { useEffect, useRef, useState } from 'react';
import Stack from '@mui/material/Stack';
import { useAppDispatch, useAppSelector } from 'store';
import {
  selectCurrentDashboard,
  selectFleetData,
} from 'store/fleet-management/fleet-management-slice-selectors';
import Typography from '@mui/material/Typography';
import WidgetContainer from './widget/WidgetContainer';
import Button from '@mui/material/Button';
import { CopyIcon, CrossCloseIcon, EditIcon } from '@common/utils/icons/icons';
import { useTranslation } from 'react-i18next';
import DashboardForm from './DashboardForm';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { getFleetData } from 'store/fleet-management/fleet-management-actions';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import FleetWidget from 'models/fleet-management/FleetWidget';
import WidgetEditGrid from './widget/WidgetEditGrid';

const DashboardBlock: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const dashboard = useAppSelector((state) => selectCurrentDashboard(state));
  const data = useAppSelector((state) => selectFleetData(state));

  useEffect(() => {
    new Set(dashboard?.widgets.flatMap((w) => w.dataset)).forEach((dataSetId) => {
      const dataGetStatus = data.find((d) => d.id === dataSetId)?.rest.getStatus;
      if (dataGetStatus == null || dataGetStatus === HttpFetchStatus.Idle) {
        dispatch(getFleetData(dataSetId));
      }
    });
  }, [dashboard?.widgets, data, dispatch]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [isFormDuplicateOpen, setIsFormDuplicateOpen] = useState<boolean | null>(null);
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
  const [widgets, setWidgets] = useState(dashboard?.widgets ?? []);

  const [unitWidth, setUnitWidth] = useState(0);

  const recalculateUnitWidth = () => {
    const width = containerRef.current?.clientWidth ?? 0;
    setUnitWidth(width / 12 - 1);
  };

  useEffect(() => {
    if (dashboard == null || !containerRef.current) return;
    recalculateUnitWidth();
  }, [dashboard]);

  useEffect(() => {
    setWidgets(dashboard?.widgets ?? []);
  }, [dashboard?.widgets, isFormDuplicateOpen]);

  const widgetUpdateHandler = (newWidgets: FleetWidget[]) => {
    setWidgets(newWidgets);
  };

  const openFormClickHandler = (isDuplicate: boolean) => {
    setIsFormDuplicateOpen(isDuplicate);
  };

  const closeFormHandler = () => {
    setIsFormDuplicateOpen(null);
  };

  const selectedWidgetChangeHandler = (id: string | null) => {
    setSelectedWidgetId(id);
  };

  if (dashboard == null) return null;

  const singleWidgetDashboard = dashboard.widgets.length === 1;

  return (
    <React.Fragment>
      <Stack height="100%" flex={1} overflow="hidden">
        <Stack direction="row" justifyContent="space-between" alignItems="end" py={1}>
          <Stack>
            <Typography variant="h4">{dashboard.name}</Typography>
            <Typography variant="body1">{dashboard.description}</Typography>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              startIcon={<CopyIcon />}
              onClick={() => openFormClickHandler(true)}
              disabled={isFormDuplicateOpen !== null}
            >
              {t('button-duplicate')}
            </Button>
            {!dashboard.isDefault && isFormDuplicateOpen === null && (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => openFormClickHandler(false)}
              >
                {t('button-edit')}
              </Button>
            )}
            {isFormDuplicateOpen !== null && (
              <IconButton onClick={closeFormHandler}>
                <CrossCloseIcon />
              </IconButton>
            )}
          </Stack>
        </Stack>
        <Stack
          overflow={singleWidgetDashboard ? 'hidden' : 'auto'}
          direction="row"
          spacing={1}
          height="100%"
        >
          <Stack
            flexGrow={1}
            sx={{
              p: isFormDuplicateOpen !== null ? 2 : undefined,
              overflowX: 'hidden',
              overflowY:
                singleWidgetDashboard && isFormDuplicateOpen === null ? 'hidden' : undefined,
              height: '100%',
              position: 'relative',
            }}
          >
            <Stack
              ref={containerRef}
              sx={{
                height: '100%',
                position: 'relative',
              }}
            >
              {(isFormDuplicateOpen != null ? widgets : dashboard.widgets).map((widget) => (
                <WidgetContainer
                  key={widget.id}
                  widget={widget}
                  fullHeight={singleWidgetDashboard && isFormDuplicateOpen === null}
                  unitWidth={unitWidth}
                  isFormOpen={isFormDuplicateOpen !== null}
                  selectedWidget={{
                    selectedId: selectedWidgetId,
                    onChange: selectedWidgetChangeHandler,
                  }}
                />
              ))}
              {isFormDuplicateOpen !== null && (
                <WidgetEditGrid
                  widgets={widgets}
                  unitWidth={unitWidth}
                  onChange={widgetUpdateHandler}
                  selectedWidget={{ id: selectedWidgetId, onChange: selectedWidgetChangeHandler }}
                />
              )}
            </Stack>
          </Stack>

          <Collapse
            in={isFormDuplicateOpen !== null}
            orientation="horizontal"
            onEntered={recalculateUnitWidth}
            onExited={recalculateUnitWidth}
            sx={(theme) => ({
              flexShrink: 0,
              maxWidth: theme.spacing(48),
              '& .MuiCollapse-wrapperInner': {
                width: theme.spacing(48),
                overflow: 'hidden',
              },
            })}
          >
            <DashboardForm
              dashboard={dashboard}
              editingWidgets={{
                id: selectedWidgetId,
                widgets,
                onChange: widgetUpdateHandler,
                onSelectedChange: selectedWidgetChangeHandler,
              }}
              onClose={closeFormHandler}
              duplicate={isFormDuplicateOpen === true}
            />
          </Collapse>
        </Stack>
      </Stack>
    </React.Fragment>
  );
};

export default DashboardBlock;
